/**
 * Sistemas integrados à Fidexa — fonte única para a página /integracoes e o
 * menu "Integrações" do header.
 */
export type IntegrationMethod = "API" | "PLANILHA";

export type Integration = {
  /** Âncora do card em /integracoes (e, no futuro, slug da página do sistema). */
  slug: string;
  name: string;
  /** Sigla do monograma enquanto o logo oficial não está em public/. */
  initials: string;
  methods: IntegrationMethod[];
  description: string;
  /** Frase curta do menu "Integrações" do header: o que o sistema é. */
  menuDescription: string;
};

/**
 * Mesmos sistemas do enum `ErpSystem` do backend (src/domain/company/companyTypes.ts),
 * exceto `DataSystemUseSheets` (ver OTHER_ERPS). Sistema novo lá → card novo
 * aqui.
 */
export const API_INTEGRATIONS: Integration[] = [
  {
    slug: "linx-seta-digital",
    name: "Linx Seta Digital",
    initials: "LX",
    methods: ["API", "PLANILHA"],
    description:
      "Clientes, parcelas e vencimentos chegam direto do ERP, sem exportar arquivo. Também aceita importação por planilha.",
    menuDescription: "ERP da TOTVS Linx para o varejo.",
  },
  {
    slug: "ssotica",
    name: "Ssótica",
    initials: "SS",
    methods: ["API"],
    description:
      "A carteira do crediário sincroniza com o sistema da ótica, sem exportar arquivo.",
    menuDescription: "Sistema de gestão para óticas.",
  },
  {
    slug: "boom-sistemas",
    name: "Boom Sistemas",
    initials: "BS",
    methods: ["API"],
    description:
      "Os dados do crediário são lidos automaticamente do sistema, sem exportar arquivo.",
    menuDescription: "Sistema de gestão para o varejo.",
  },
  // Omie e Base ERP Asaas: fora do enum ErpSystem do backend.
  {
    slug: "omie-erp",
    name: "Omie ERP",
    initials: "OM",
    methods: ["API"],
    description:
      "Clientes, parcelas e vencimentos chegam direto do ERP, sem exportar arquivo.",
    menuDescription: "ERP online para pequenas e médias empresas.",
  },
  {
    slug: "base-erp-asaas",
    name: "Base ERP Asaas",
    initials: "BA",
    methods: ["API"],
    description:
      "Clientes, parcelas e vencimentos chegam direto do ERP, sem exportar arquivo.",
    menuDescription: "ERP para pequenas e médias empresas.",
  },
];

export const SHEET_INTEGRATIONS: Integration[] = [
  {
    slug: "praticx-erp",
    name: "Praticx ERP",
    initials: "PX",
    methods: ["PLANILHA"],
    description:
      "Exporte a planilha com clientes, valores devidos e datas de vencimento e a Fidexa importa a carteira.",
    menuDescription: "ERP para o varejo.",
  },
  {
    slug: "lubb-erp",
    name: "Lubb ERP",
    initials: "LB",
    methods: ["PLANILHA"],
    description:
      "Importação pelo arquivo XML exportado do sistema, com clientes, valores e vencimentos.",
    menuDescription: "ERP para o varejo.",
  },
  {
    // `NacionalErp` no enum do backend.
    slug: "protheus-totvs",
    name: "Protheus TOTVS",
    initials: "PT",
    methods: ["PLANILHA"],
    description:
      "Importação pelo arquivo XLS exportado do sistema, com clientes, valores e vencimentos.",
    menuDescription: "ERP da TOTVS.",
  },
  {
    slug: "arq-erp",
    name: "ARQ ERP",
    initials: "AQ",
    methods: ["PLANILHA"],
    description:
      "Importação pelo arquivo CSV exportado do sistema, com clientes, valores e vencimentos.",
    menuDescription: "ERP para o varejo.",
  },
];

/**
 * Fecha o menu do header no lugar do ERP de `DataSystemUseSheets`, cujo nome
 * não pode aparecer em rota pública (ver CLAUDE.md, "Nomes proibidos"). Não
 * vira card na página: lá, quem não achou o sistema cai na faixa
 * "Seu sistema não está na lista?" (âncora `#outros-erps`).
 */
export const OTHER_ERPS: Integration = {
  slug: "outros-erps",
  name: "Outros ERPs",
  initials: "OE",
  methods: ["PLANILHA"],
  description:
    "Seu ERP não está aqui? Exporte a planilha com clientes, valores devidos e datas de vencimento e a Fidexa importa a carteira.",
  menuDescription: "Qualquer ERP que exporte planilha.",
};

/** Os que aparecem no menu suspenso do header — 4, um por coluna do painel. */
export const FEATURED_INTEGRATIONS: Integration[] = [
  ...API_INTEGRATIONS.slice(0, 3),
  OTHER_ERPS,
];

export const integrationAnchor = (slug: string) => `/integracoes#${slug}`;
